import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SparklesIcon, Squares2X2Icon, CameraIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { locationService, Location } from '../services/location';
import homepageService from '../services/homepage';
import { HomepageBanner, Designer } from '../types';
import BannerSlider from '../components/BannerSlider';
import FeaturedDesigners from '../components/FeaturedDesigners';

const Home: React.FC = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  
  // Homepage data state
  const [banners, setBanners] = useState<HomepageBanner[]>([]);
  const [featuredDesigners, setFeaturedDesigners] = useState<Designer[]>([]);
  const [loadingBanners, setLoadingBanners] = useState(true);
  const [loadingDesigners, setLoadingDesigners] = useState(true);

  useEffect(() => {
    loadLocations();
    loadHomepageData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  const loadLocations = async () => {
    try {
      const data = await locationService.getLocations();
      setLocations(data);
    } catch (error) {
      console.error('Failed to load locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadHomepageData = async () => {
    try {
      // Load banners and featured designers in parallel
      const [bannersData, designersData] = await Promise.all([
        homepageService.getBanners().catch(error => {
          console.error('Failed to load banners:', error);
          return [];
        }),
        homepageService.getFeaturedDesigners().catch(error => {
          console.error('Failed to load featured designers:', error);
          return [];
        })
      ]);
      
      setBanners(bannersData);
      setFeaturedDesigners(designersData);
    } catch (error) {
      console.error('Failed to load homepage data:', error);
    } finally {
      setLoadingBanners(false);
      setLoadingDesigners(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="px-4 py-6">
          <div>
            <h1 className="text-2xl font-serif text-gray-900">
              Welcome, {user?.name}!
            </h1>
            <div className="flex items-center mt-1">
              <p className="text-sm text-gray-600">
                {user?.catalogAccessLevel === 'exclusive'
                  ? 'Exclusive Access'
                  : user?.catalogAccessLevel === 'partner'
                  ? 'Partner Access'
                  : user?.catalogAccessLevel === 'paid_deposit'
                  ? 'Paid Deposit Access'
                  : 'New Member Access'}
              </p>
              {user?.catalogAccessLevel !== 'exclusive' && user?.catalogAccessLevel !== 'partner' && (
                <div className="relative ml-1" ref={tooltipRef}>
                  <button
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Learn more about catalog access"
                  >
                    <QuestionMarkCircleIcon className="h-4 w-4" />
                  </button>
                  {showTooltip && (
                    <div 
                      className="absolute z-10 left-0 top-6 w-64 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg"
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                    >
                      <div className="relative">
                        <div className="absolute -top-5 left-2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-gray-900"></div>
                        <p>
                          Get <strong>Full Access</strong> to our latest catalog by arranging an appointment.{' '}
                          <a 
                            href="https://www.theproposal.com.sg/contact/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-300"
                          >
                            Contact Us
                          </a>{' '}
                          Today!
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Banner Slider */}
      <BannerSlider 
        banners={banners}
        className="mb-8"
      />
      
      {/* Fallback Hero Section (shown when no banners) */}
      {!loadingBanners && banners.length === 0 && (
        <div className="bg-primary-50 px-4 py-8">
          <div className="text-center">
            <h2 className="text-3xl font-serif text-gray-900 mb-4">
              Find Your Dream Dress
            </h2>
            <p className="text-gray-700 mb-6">
              Discover the perfect wedding dress from our curated collection
            </p>
            <Link to="/home/style-matcher" className="btn-primary inline-block">
              Start Style Matcher
            </Link>
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div className="px-4 py-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Access</h3>
        <div className="grid grid-cols-1 gap-4">
          <Link
            to="/home/catalog"
            className="bg-white rounded-lg shadow-sm p-6 flex items-center space-x-4 hover:shadow-md transition-shadow"
          >
            <Squares2X2Icon className="h-10 w-10 text-primary-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900">Browse Catalog</h4>
              <p className="text-sm text-gray-600">
                Explore our collection of beautiful wedding dresses
              </p>
            </div>
          </Link>

          <div
            className="bg-white rounded-lg shadow-sm p-6 flex items-center space-x-4 opacity-60 cursor-not-allowed"
          >
            <SparklesIcon className="h-10 w-10 text-gray-400 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900">Style Matcher <span className="text-sm text-gray-500 font-normal">(Coming Soon)</span></h4>
              <p className="text-sm text-gray-600">
                Feature in development
              </p>
            </div>
          </div>

          <Link
            to="/home/photos"
            className="bg-white rounded-lg shadow-sm p-6 flex items-center space-x-4 hover:shadow-md transition-shadow"
          >
            <CameraIcon className="h-10 w-10 text-primary-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900">Your Photos</h4>
              <p className="text-sm text-gray-600">
                View photos from your bridal appointments
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* Featured Designers Section */}
      <FeaturedDesigners 
        designers={featuredDesigners}
        loading={loadingDesigners}
      />
      
      {/* Store Info */}
      <div className="px-4 py-8 bg-white">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Locations</h3>
        {loadingLocations ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : locations.length === 0 ? (
          <p className="text-gray-600 text-center py-8">No locations available at this time.</p>
        ) : (
          <div className="space-y-4">
            {locations.map((location) => (
              <div key={location.id} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900">{location.name}</h4>
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">
                  {location.address}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;